import { createContext, useState, useContext, useEffect } from "react";
import FolderService from "services/folder";
import TagService from "services/tag";
import ExportModal from "components/Export/ExportModal";
import CreateLabelsModal from "components/Label/CreateLabelsModal";
import PdfViewerModal from "components/Pdf/PdfModal";
import UpdateSingleQuantityModal from "components/item/UpdateSingleQuantityModal";
import UpdateMultiQuantityModal from "components/item/UpdateMultiQuantityModal";
import { useItemsScreenContext } from "pages/items";
import eventBus from "../../bus";
import MoveSingleQuantityItemModal from "components/MoveQuantity/MoveSingleQuantityItemModal";
import MoveMultiQuantityItemsModal from "components/MoveQuantity/MoveMultiQuantityItemsModal";
import { io } from "socket.io-client";
import { socket_url } from "config";
import MoveFoldersModal from "components/MoveFolder/MoveFoldersModal";
import AddOrRemoveTagItemModal from "components/Tag/AddOrRemoveTagItemModal";
import LowStockAlertModal from "components/Alert/LowStockAlertModal";
import CustomFieldService from "services/custom_field";
import DeleteModal from "components/Delete/DeleteModal";
import AttributeService from "services/attribute";
import AppLoader from "components/app-loader";
import NotificationService from "services/notification";
import CloneModal from "components/Clone/CloneModal";
import { useNotification } from "context/notification";

const socket = io(socket_url, {
    auth: {
        token: localStorage.getItem("token"),
    },
});

const AppContext = createContext();

function AppProvider({ children }) {
    const [folderList, setFolderList] = useState([]);
    const [tagList, setTagList] = useState([]);
    const [customFieldList, setCustomFieldList] = useState([]);
    const [attributeList, setAttributeList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [rootFolder, setRootFolder] = useState({});
    const [countUnRead, setCountUnRead] = useState();

    const { success, error } = useNotification();

    const [modals, setModals] = useState({
        createLabel: { visible: false, items: [], folderId: null },
        export: { visible: false, items: [], folderId: null },
        pdf: { visible: false, pdfUrl: null },
        updateSingleQuantity: { visible: false, item: null, folderId: null },
        updateMultiQuantity: { visible: false, items: [], folderId: null },
        moveSingleQuantity: { visible: false, item: null, folderId: null },
        moveMultiQuantity: { visible: false, items: [], folderId: null },
        moveFolders: { visible: false, items: [], folderId: null },
        setTag: { visible: false, items: [], folderId: null, type: "" },
        alert: { visible: false, items: [], folderId: null },
        delete: { visible: false, items: [], folderId: null },
        clone: { visible: false, items: [], folderId: null },
    });

    useEffect(() => {
        // loadFolders();
        // loadTags();
        // loadCustomFields();
        // loadAttributes();
        loadData();
    }, []);

    useEffect(() => {
        socket.on("data_changed", (payload) => {
            var { data_type } = payload;
            if (data_type == "folder") {
                loadFolders();
            }
            if (data_type == "tag") {
                loadTags();
            }
            if (data_type == "custom_field") {
                loadCustomFields();
            }
        });

        socket.on("notification", (payload) => {
            console.log("notification", payload);
            var { data_type, message } = payload;
            loadCountUnread();
            error(message);
        });
    }, []);

    const loadData = async () => {
        var array = [loadFolders(), loadTags(), loadCustomFields(), loadAttributes(), loadCountUnread()];
        await Promise.all(array);

        setIsLoading(false);
    };

    const loadFolders = async () => {
        var service = new FolderService();
        var lst = await service.list();
        setFolderList(lst);
        var root = lst.find((x) => x.is_root);
        setRootFolder(root ?? {});
    };

    const loadTags = async () => {
        const tagService = new TagService();
        const lst = await tagService.list();
        setTagList(lst);
    };

    const loadCustomFields = async () => {
        const customFieldService = new CustomFieldService();
        const lst = await customFieldService.get();
        setCustomFieldList(lst);
    };

    const loadAttributes = async () => {
        const attributeService = new AttributeService();
        const lst = await attributeService.get();
        setAttributeList(lst);
    };

    const loadCountUnread = async () => {
        const notificationService = new NotificationService();
        const result = await notificationService.countUnread();
        setCountUnRead(result);
    };

    const showModal = (type, options) => {
        setModals((prev) => ({
            ...prev,
            [type]: { ...prev[type], visible: true, ...options },
        }));
    };

    const hideModal = (type) => {
        const check = ["moveSingleQuantity", "updateSingleQuantity"].includes(type);
        setModals((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                visible: false,
                item: check ? {} : undefined,
                items: check ? undefined : [],
                folderId: null,
            },
        }));
    };

    if (isLoading) {
        return <AppLoader></AppLoader>;
    }

    return (
        <AppContext.Provider
            value={{
                folderList,
                showModal,
                eventBus,
                tagList,
                customFieldList,
                attributeList,
                rootFolder,
                countUnRead,
                setCountUnRead,
            }}
        >
            <CreateLabelsModal {...modals.createLabel} onClose={() => hideModal("createLabel")} />

            <ExportModal {...modals.export} onClose={() => hideModal("export")} />

            <PdfViewerModal {...modals.pdf} onClose={() => hideModal("pdf")} />

            <UpdateSingleQuantityModal {...modals.updateSingleQuantity} onClose={() => hideModal("updateSingleQuantity")} />

            <UpdateMultiQuantityModal {...modals.updateMultiQuantity} onClose={() => hideModal("updateMultiQuantity")} />

            <MoveSingleQuantityItemModal {...modals.moveSingleQuantity} onClose={() => hideModal("moveSingleQuantity")} />

            <MoveMultiQuantityItemsModal {...modals.moveMultiQuantity} onClose={() => hideModal("moveMultiQuantity")} />

            <MoveFoldersModal {...modals.moveFolders} onClose={() => hideModal("moveFolders")} />

            <AddOrRemoveTagItemModal {...modals.setTag} onClose={() => hideModal("setTag")} />

            <LowStockAlertModal {...modals.alert} onClose={() => hideModal("alert")} />

            <DeleteModal {...modals.delete} onClose={() => hideModal("delete")} />

            <CloneModal {...modals.clone} onClose={() => hideModal("clone")} />
            {children}
        </AppContext.Provider>
    );
}

const useAppContext = () => {
    const context = useContext(AppContext);

    return context;
};

export { useAppContext };
export default AppProvider;
